import { v4 } from 'uuid';
import { HomePageView } from './mainPage/HomePageView';
import { createDioramaShareImageBase64 } from '../game/views/dioramas/createDioramaShareImage';
import type { DioramaView } from '../game/views/dioramas/DioramaView';
import type { App } from '../app/App';
import type { MainMenuController } from './MainMenuController';
import { PiggyBankIconView } from '../gameEvents/piggyBank/views/PiggyBankIconView';
import { openPiggyBankOverlay } from '../gameEvents/piggyBank/behaviors/openPiggyBankOverlay';
import { createPiggyBankDependencies } from '../gameEvents/adapters/createPiggyBankDependencies';
import { DailyRewardIconView } from './mainPage/DailyRewardIconView';
import { openDailyRewardPopup } from '../behaviours/openDailyRewardPopup';
import type { MenuIcon } from './types';
import { OfferMenuButtonView } from './offers/OfferMenuButtonView';
import type { IAPProduct } from '../app/iap/types';
import { IAPPlacement } from '../app/iap/types';
import { OfferLocation } from '../app/offers/offersTypes';
import { openOfferPopup } from '../behaviours/findOfferAndOpenPopup';
import { OfferInteractionLocation } from '../app/tracking/types';

interface HomePageControllerConfig {
  mainMenuController: MainMenuController;
  app: App;
  dioramaView?: DioramaView;
}

export class HomePageController {
  public readonly onAnimationFinished: Promise<void>;

  private readonly onAnimationFinishedResolver: () => void;

  private readonly homePageView: HomePageView;

  constructor(private readonly config: HomePageControllerConfig) {
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const { promise, resolve } = Promise.withResolvers<void>();
    this.onAnimationFinished = promise;
    this.onAnimationFinishedResolver = resolve;

    this.homePageView = this.createHomePageView();
  }

  public getHomePageView(): HomePageView {
    return this.homePageView;
  }

  private createHomePageView(): HomePageView {
    const {
      config: {
        app: {
          stateController,
          renderer,
          platform: { shareable },
          preloader,
          gameEvents: { collectionEvent },
        },
        mainMenuController,
        dioramaView,
      },
    } = this;

    const homePageView = new HomePageView({
      nextLevelNumber: stateController.getLevelNumber(),
      state: stateController,
      dioramaView,
      onDioramaAnimationFinished: this.onAnimationFinishedResolver,
      collectionEventProgressView: collectionEvent?.createProgressBarView() ?? null,
    });

    homePageView.onActionButtonClicked.connect(async () => {
      await mainMenuController.playButtonAction();
    });

    homePageView.addResourceIcon(this.createOfferIcon());
    homePageView.addResourceIcon(this.createPiggyBankIcon());
    homePageView.addResourceIcon(this.createDailyRewardIcon());

    if (stateController.getLevelNumber() > 2) {
      this.config.app.gameEvents.createMenuIcons().forEach((icon) => {
        homePageView.addEventIcon(icon);
      });
      if (this.config.app.gameEvents.versus) {
        homePageView.addResourceIcon(this.config.app.gameEvents.versus.createGameEventIconNotAutomatic());
      }
    }

    homePageView.onDioramaClicked.connect(async () => {
      const view = homePageView.openDioramaDisplay({ canShare: !!shareable, preloader, state: stateController });
      if (!view) return;

      view.onDioramaShare.connect(async (config) => {
        const image = await createDioramaShareImageBase64(renderer.pixi.renderer, config);
        void shareable?.share?.(image);
      });

      view.onCloseButtonClicked.connect(() => {
        void homePageView.closeDioramaDisplay();
        void mainMenuController?.mainMenuView?.showUI?.();
      });

      await mainMenuController?.mainMenuView?.hideUI?.();
      void view.show();
    });

    return homePageView;
  }

  private createPiggyBankIcon(): PiggyBankIconView {
    const {
      config: {
        app,
        mainMenuController,
      },
    } = this;

    const piggyBankDependencies = createPiggyBankDependencies(app);
    const iconView = new PiggyBankIconView(piggyBankDependencies.observable);

    const openPiggyBank = (): void => {
      void openPiggyBankOverlay({
        dependencies: piggyBankDependencies,
        onPlayClicked: async () => {
          await mainMenuController.playButtonAction();
        },
        onPiggyBankTierIncreased: () => {
          openPiggyBank();
        },
      });
    };

    iconView.onClicked.connect(() => { openPiggyBank(); });

    return iconView;
  }

  private createDailyRewardIcon(): DailyRewardIconView | null {
    const {
      config: { app, mainMenuController },
    } = this;

    if (!app.ads) return null;

    const iconView = new DailyRewardIconView(app.stateController);
    iconView.onClicked.connect(() => {
      openDailyRewardPopup(app, mainMenuController.mainMenuView?.topBar);
    });

    return iconView;
  }

  private createOfferIcon(): MenuIcon | null {
    const {
      app: { iap, tracking },
    } = this.config;

    if (!iap) return null;

    const icon = new OfferMenuButtonView();

    const impressionId = v4();

    icon.onClicked.connect(async (product) => {
      tracking.trackOfferClick({
        product,
        location: OfferInteractionLocation.MenuIcon,
        impressionId,
      });

      if (product.data.popupLayout) {
        void openOfferPopup(this.config.app, product, IAPPlacement.MainMenu);
      } else {
        await iap?.makePurchase({
          product,
          placement: IAPPlacement.MainMenu,
          showResults: true,
          offerImpressionId: impressionId,
        });
      }
    });

    const getOfferForIcon = (products: IAPProduct[]): void => {
      const product = products.find((p) => p.locations.includes(OfferLocation.MenuButton));
      icon.setCurrentOffer(product ?? null);
    };

    const connection = iap.onOffersUpdated.connect(getOfferForIcon);
    void iap.getCatalogAsync(OfferLocation.MenuButton, null).then((products) => {
      getOfferForIcon(products);
    });

    icon.on('destroyed', () => {
      connection.disconnect();
    });

    return icon;
  }
}
